import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Notification Service
 * Handles fetching and managing admin notifications
 */

/**
 * Get recent admin notifications
 * @param {number} [maxCount=10] - Maximum number of notifications to fetch
 * @param {boolean} [unreadOnly=false] - Only fetch unread notifications
 * @returns {Promise<Array>} - Array of notification objects
 */
export const getAdminNotifications = async (maxCount = 10, unreadOnly = false) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    let q;

    if (unreadOnly) {
      q = query(
        notificationsRef,
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(maxCount)
      );
    } else {
      q = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(maxCount)
      );
    }

    const snapshot = await getDocs(q);
    const notifications = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // Convert Firestore Timestamp to ISO string if needed
      let createdAt = data.createdAt;
      if (createdAt && typeof createdAt.toDate === 'function') {
        createdAt = createdAt.toDate().toISOString();
      } else if (createdAt && createdAt.seconds) {
        createdAt = new Date(createdAt.seconds * 1000).toISOString();
      }

      notifications.push({
        id: doc.id,
        type: data.type || 'info',
        title: data.title || 'Notification',
        message: data.message || '',
        category: data.category || 'system',
        read: data.read || false,
        readAt: data.readAt || null,
        createdAt: createdAt || new Date().toISOString(),
        priority: data.priority || 'normal', // 'low', 'normal', 'high', 'urgent'
        action: data.action || null, // { type: 'route', path: '/analytics', section: 'notifications' }
        metadata: data.metadata || {}
      });
    });

    // Sort by creation date (newest first)
    notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return notifications;
  } catch (error) {
    console.error('❌ Error fetching admin notifications:', error);
    // Return empty array on error rather than throwing
    return [];
  }
};

/**
 * Get unread notification count
 * @returns {Promise<number>} - Count of unread notifications
 */
export const getUnreadNotificationCount = async () => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('❌ Error fetching unread count:', error);
    return 0;
  }
};

/**
 * Mark notification as read
 * @param {string} notificationId - Notification document ID
 * @returns {Promise<void>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('❌ Error marking notification as read:', error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * @returns {Promise<void>}
 */
export const markAllNotificationsAsRead = async () => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );
    
    const snapshot = await getDocs(q);
    const updatePromises = [];

    snapshot.forEach((doc) => {
      const notificationRef = doc.ref;
      updatePromises.push(
        updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp()
        })
      );
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('❌ Error marking all notifications as read:', error);
    throw error;
  }
};

/**
 * Delete notification
 * @param {string} notificationId - Notification document ID
 * @returns {Promise<void>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('❌ Error deleting notification:', error);
    throw error;
  }
};

/**
 * Get notification preferences from localStorage
 * Uses the admin email from localStorage to create a unique key
 * @returns {Object} - Notification preferences
 */
export const getNotificationPreferences = () => {
  if (typeof window === 'undefined') return {};
  
  // Get admin email - try STORAGE_KEYS first, fallback to direct key
  let adminEmail;
  try {
    const { STORAGE_KEYS } = require('../constants');
    adminEmail = localStorage.getItem(STORAGE_KEYS.ADMIN_EMAIL) || 'default';
  } catch (e) {
    // If require fails, use direct key
    adminEmail = localStorage.getItem('admin-email') || 'default';
  }
  
  // Sanitize email to use as localStorage key (replace @ and . with _)
  const emailKey = adminEmail.replace(/@/g, '_').replace(/\./g, '_');
  
  return {
    emailNotifications: localStorage.getItem(`${emailKey}_notifications_email`) !== 'false',
    pushNotifications: localStorage.getItem(`${emailKey}_notifications_push`) !== 'false',
    failedNotificationsAlert: localStorage.getItem(`${emailKey}_notifications_failed_alert`) !== 'false',
    lowDeliveryRateAlert: localStorage.getItem(`${emailKey}_notifications_delivery_alert`) !== 'false',
    alertThreshold: parseInt(localStorage.getItem(`${emailKey}_notifications_threshold`)) || 50
  };
};

/**
 * Generate intelligent notifications based on analytics data
 * This creates actionable notifications from system metrics
 * Uses notification preferences to determine what alerts to show
 * @param {Object} analyticsData - Analytics data from various sources
 * @returns {Array} - Array of generated notification objects
 */
export const generateIntelligentNotifications = (analyticsData) => {
  const notifications = [];
  const preferences = getNotificationPreferences();

  // Check notification analytics
  if (analyticsData.notificationData) {
    const { failedNotifications, deliveryRate } = analyticsData.notificationData;

    // Failed notifications alert (respects user preference)
    if (preferences.failedNotificationsAlert && failedNotifications > preferences.alertThreshold) {
      notifications.push({
        type: 'error',
        title: 'High Failed Notifications',
        message: `${failedNotifications} notifications failed delivery in the last 24 hours`,
        category: 'notifications',
        priority: 'high',
        action: {
          type: 'route',
          path: null, // Will be resolved by component using getAdminPrefix()
          section: 'notifications'
        },
        createdAt: new Date().toISOString()
      });
    }

    // Low delivery rate alert (respects user preference)
    if (preferences.lowDeliveryRateAlert && deliveryRate < 85 && deliveryRate > 0) {
      notifications.push({
        type: 'warning',
        title: 'Low Delivery Rate',
        message: `Notification delivery rate is ${deliveryRate.toFixed(1)}%`,
        category: 'notifications',
        priority: 'normal',
        action: {
          type: 'route',
          path: null, // Will be resolved by component using getAdminPrefix()
          section: 'notifications'
        },
        createdAt: new Date().toISOString()
      });
    }
  }

  // Check user analytics
  if (analyticsData.userData) {
    const { periodUserCount } = analyticsData.userData;
    if (periodUserCount > 50) {
      notifications.push({
        type: 'info',
        title: 'New User Growth',
        message: `${periodUserCount} new users in the last period`,
        category: 'users',
        priority: 'normal',
        action: {
          type: 'route',
          path: null,
          section: 'users'
        },
        createdAt: new Date().toISOString()
      });
    }
  }

  // Check waitlist analytics
  if (analyticsData.waitlistData) {
    const { waitlistCount } = analyticsData.waitlistData;
    if (waitlistCount > 100) {
      notifications.push({
        type: 'info',
        title: 'Growing Waitlist',
        message: `${waitlistCount} users on the waitlist`,
        category: 'waitlist',
        priority: 'low',
        action: {
          type: 'route',
          path: null,
          section: 'waitlist'
        },
        createdAt: new Date().toISOString()
      });
    }
  }

  // Sort by priority and date
  const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
  notifications.sort((a, b) => {
    const priorityDiff = (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  return notifications;
};

